import React from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import vivaConfirmation from 'images/brands/etn/vivaConfirmation.webp';
import vivaDiscountTickets from 'images/brands/etn/vivaDiscountTickets.webp';
import useViajaAhorrandoDiscountPercent from 'hooks/store/viajaAhorrando/useViajaAhorrandoDiscountPercent';

const CONFETTI_COLORS = [
  'rgb(236, 34, 34)',
  'rgb(236, 212, 34)',
  'rgb(34, 236, 228)',
  'rgb(236, 34, 213)',
  'rgb(52, 236, 34)',
  'rgb(34, 101, 236)',
  'rgb(236, 34, 134)',
  'rgb(208, 127, 255)',
  '#ff4d29',
  '#004a23',
];

/**
 * VivaPromoBannerConfirmation component.
 * Render a promotional confirmation banner for Viva airline with discount information and confetti animation.
 * @param {object} props - Component props.
 * @param {boolean} props.isViajaAhorrando - Controls whether the component should be rendered. Defaults to true.
 */
const VivaPromoBannerConfirmation = ({ isViajaAhorrando = true }) => {
  const { t } = useTranslation('general');
  const discountPercent = useViajaAhorrandoDiscountPercent();

  if (!isViajaAhorrando) {
    return null;
  }

  // Format discount percentage for display
  const discountDisplay = discountPercent !== null ? `${discountPercent}%` : '50%';

  return (
    <div className="w-full flex justify-center box-border px-0">
      <div
        className="relative flex items-center bg-white rounded-2xl shadow-[0_2px_4px_rgba(0,0,0,0.08)] 
      p-4 overflow-hidden w-full"
      >
        <div className="w-1/2 md:w-3/5">
          <span className="text-sm">
            <Trans
              t={t}
              i18nKey="viva_confirmation_message"
              components={{
                bold: <span className="text-base font-bold leading-relaxed" />,
                discount: <span className="text-[#ff4b21] font-bold" />,
              }}
              values={{ discount: discountDisplay }}
            />
          </span>
        </div>
        <div
          className="absolute inset-y-0 right-0 w-2/5 md:w-1/5 lg:w-1/4 min-w-44 md:min-w-64 lg:min-w-80
          bg-[#08a33f] rounded-tl-full flex items-center justify-between md:justify-center md:items-center
          marker:overflow-visible z-[1]"
        >
          <img
            src={vivaDiscountTickets}
            alt="Tickets con descuento"
            className="block lg:hidden relative z-[2] w-24 mx-auto "
          />
          <div className="relative w-full h-full hidden lg:block">
            <img
              src={vivaConfirmation}
              alt="Confirmación de pago con descuento"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
              lg:w-4/5 h-auto block z-[2]"
            />
            <div className="absolute inset-0 pointer-events-none z-[1] lg:top-0 lg:left-0 lg:w-full lg:h-full">
              {CONFETTI_COLORS.map((color, index) => {
                const xOffset = (index % 4) * 2 - 3;
                const scale = 0.5 + (index % 3) * 0.2;
                const particleId = `confetti-${color}-${index}`;
                return (
                  <div
                    key={particleId}
                    className="
                      absolute
                      w-1 h-3
                      rounded
                      bg-[var(--confetti-color)]
                      bottom-[10%]
                      left-[var(--confetti-x)]
                      opacity-0
                      animate-[confettiRise_var(--confetti-duration)_ease-out_var(--confetti-delay)_infinite]
                    "
                    style={{
                      '--confetti-color': color,
                      '--confetti-delay': `${index * 0.08}s`,
                      '--confetti-x': `${50 + (index % 8) * 6 - 24}%`,
                      '--confetti-x-offset': xOffset,
                      '--confetti-scale': scale,
                      '--confetti-duration': `${1.8 + (index % 4) * 0.2}s`,
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

VivaPromoBannerConfirmation.propTypes = {
  isViajaAhorrando: PropTypes.bool,
};

export default VivaPromoBannerConfirmation;
